import _ from "lodash";

// Prescription zones and their application rates.
//
// Zone 1 is the lowest-yield zone (red on the map) and the top zone is the
// highest (green). Whatever rate the editor shows next to a zone is the rate
// that zone gets in every export and upload: the backend writes
// `rates[zone - 1]`, so `rates[i]` must be the rate of Zone i + 1. Every sender
// builds its list with buildZoneRates; never reorder it on the way out.

// Zone contains information about zone of the prescription map.
export type Zone = {
  // Zone id, 1 = lowest yield
  id: number;
  // Zone rate
  rate: number;
  // Text representation of the rate
  raw_rate: string;
  // Area in square meters
  area: number;
  // Color of the zone on the map
  color: string;
};

// Default rate for a zone the user has not edited: the standard rate at the
// middle zone, +5% per step towards zone 1 and -5% per step towards the top
// zone. A typed value (raw_rate) always wins.
export const getRate = (zone: Zone, count: number, standard: number): [number, string] => {
  if (zone.raw_rate === "") {
    const mid = _.round(count / 2, 0);
    const diff = Math.abs(mid - zone.id);

    let rate: number;
    if (zone.id < mid) {
      rate = _.round(standard + ((standard / 100) * (5 * diff)), 2);
    } else if (zone.id > mid) {
      rate = _.round(standard - ((standard / 100) * (5 * diff)), 2);
    } else {
      rate = _.round(standard, 2);
    }

    return [rate, rate.toString()];
  }

  return [zone.rate, zone.raw_rate];
};

// The rates list every export and upload sends: `rates[i]` is the rate of
// Zone i + 1, looked up by zone id so the order of `zones` does not matter.
export const buildZoneRates = (zones: Zone[], standard: number): number[] => {
  const byId = new Map(zones.map((zone) => [zone.id, zone]));
  const maxId = zones.reduce((max, zone) => Math.max(max, zone.id), 0);
  const rates: number[] = [];
  for (let id = 1; id <= maxId; id++) {
    const zone = byId.get(id);
    rates.push(zone ? getRate(zone, zones.length, standard)[0] : _.round(standard, 2));
  }
  return rates;
};
